import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/lib/theme.store';
import { FeldStatus, type Status } from '@/lib/speichern';

/** Vollständiges 6-stelliges Hex — kein Kurzformat, damit der Server dieselbe Prüfung fährt */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/** Leer (= keine Farbe) oder gültiges 6-stelliges Hex. Gleiche Regel wie im Backend-DTO. */
export function isValidBrandColor(value: string): boolean {
  return value === '' || HEX_RE.test(value);
}

/** Startpunkte, damit niemand vor einem leeren Feld sitzt */
const PRESETS = [
  { hex: '#0ea5e9', label: 'Steel Blue' },
  { hex: '#2563eb', label: 'Blau' },
  { hex: '#16a34a', label: 'Grün' },
  { hex: '#ea580c', label: 'Orange' },
  { hex: '#dc2626', label: 'Rot' },
  { hex: '#7c3aed', label: 'Violett' },
  { hex: '#0f766e', label: 'Petrol' },
  { hex: '#475569', label: 'Grau' },
];

export interface BrandColorPickerProps {
  /** Gespeicherter Wert: Hex oder '' für „keine Farbe" — wird beim Verlassen wiederhergestellt */
  savedValue: string;
  /** Speichert eine gültige, geänderte Farbe */
  onSave: (hex: string) => void;
  status?: Status;
  disabled?: boolean;
}

/**
 * Farbwähler für die Mandanten-Primärfarbe (`tenant.settings.primaryColor`).
 *
 * Die Farbe wird **sofort auf das ganze ERP angewendet**, solange die Seite offen
 * ist — anders sähe man an einem Farbfeld nicht, ob sie zum Design passt.
 *
 * Gespeichert wird ohne Knopf (AP-36): beim Schließen des Farbwählers, beim
 * Verlassen des Textfelds, sofort bei einem Vorschlag oder „Zurücksetzen". Esc im
 * Textfeld stellt den gespeicherten Wert her; verlässt man die Seite mit einer
 * ungültigen Eingabe, tut das der Cleanup.
 */
export function BrandColorPicker({ savedValue, onSave, status, disabled }: BrandColorPickerProps) {
  const { t } = useTranslation();

  // Ref statt Dependency: der Cleanup soll den beim Verlassen gültigen
  // gespeicherten Wert setzen, ohne bei jedem Speichern neu zu laufen.
  const savedRef = useRef(savedValue);
  savedRef.current = savedValue;

  // Eigene Eingabe für die Vorschau; ein neuer gespeicherter Wert setzt sie zurück.
  const [value, setValue] = useState(savedValue);
  useEffect(() => setValue(savedValue), [savedValue]);

  const isValid = isValidBrandColor(value);

  /** Übernehmen: gültig und geändert → speichern. */
  const uebernimm = (hex: string) => {
    setValue(hex);
    if (isValidBrandColor(hex) && hex !== savedRef.current) onSave(hex);
  };

  // Der native `change` des Farbwählers kommt erst beim Schließen — React meldet
  // unter `onChange` jede Bewegung. Gespeichert wird nur die gewählte Farbe.
  const waehler = useRef<HTMLInputElement>(null);
  const uebernimmRef = useRef(uebernimm);
  uebernimmRef.current = uebernimm;
  useEffect(() => {
    const el = waehler.current;
    if (!el) return;
    const beiChange = () => uebernimmRef.current(el.value);
    el.addEventListener('change', beiChange);
    return () => el.removeEventListener('change', beiChange);
  }, []);

  // Live-Vorschau auf :root. getState() statt Hook-Selektor — wir wollen den
  // Setter aufrufen, ohne die Komponente an Store-Änderungen zu hängen.
  useEffect(() => {
    if (!isValid) return;
    useThemeStore.getState().setTenantAccent(value || null);
  }, [value, isValid]);

  useEffect(() => {
    return () => {
      useThemeStore.getState().setTenantAccent(savedRef.current || null);
    };
  }, []);

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-foreground">
        {t('tenants.primaryColor')}
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={waehler}
          type="color"
          disabled={disabled}
          aria-label={t('tenants.primaryColor')}
          value={HEX_RE.test(value) ? value : '#0ea5e9'}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background p-1"
        />
        <input
          type="text"
          value={value}
          disabled={disabled}
          aria-invalid={!isValid || undefined}
          onChange={(e) => {
            const raw = e.target.value.trim();
            // Führendes # ergänzen, damit „0ea5e9" ebenfalls funktioniert
            setValue(raw && !raw.startsWith('#') ? `#${raw}` : raw);
          }}
          onBlur={() => uebernimm(value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') uebernimm(value);
            if (e.key === 'Escape') setValue(savedValue);
          }}
          placeholder="#0ea5e9"
          spellCheck={false}
          className={`w-32 rounded-md border bg-background px-3 py-2 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
            isValid ? 'border-input' : 'border-destructive'
          }`}
        />
        {value !== '' && !disabled && (
          <button
            type="button"
            onClick={() => uebernimm('')}
            className="rounded-md border border-input px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"
          >
            {t('tenants.primaryColorReset')}
          </button>
        )}
      </div>

      {/* Vorschläge */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-muted-foreground">{t('tenants.primaryColorPresets')}:</span>
        {PRESETS.map((p) => (
          <button
            key={p.hex}
            type="button"
            disabled={disabled}
            title={`${p.label} · ${p.hex}`}
            aria-label={p.label}
            onClick={() => uebernimm(p.hex)}
            className={`h-6 w-6 rounded-full border-2 transition-transform hover:scale-110 ${
              value.toLowerCase() === p.hex ? 'border-foreground' : 'border-transparent'
            }`}
            style={{ backgroundColor: p.hex }}
          />
        ))}
      </div>

      {!isValid && (
        <p className="mt-2 text-xs text-destructive">{t('tenants.primaryColorInvalid')}</p>
      )}

      {isValid && value === '' && (
        <p className="mt-2 text-xs text-muted-foreground">{t('tenants.primaryColorNone')}</p>
      )}

      {/* Vorschau: dieselben Tokens, die die Farbe im Betrieb einfärbt */}
      {isValid && value !== '' && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
          <span className="text-xs text-muted-foreground">{t('tenants.primaryColorPreview')}:</span>
          <span className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
            {t('tenants.primaryColorPreviewButton')}
          </span>
          <span className="text-xs font-medium text-primary">
            {t('tenants.primaryColorPreviewLink')}
          </span>
          <span
            className="rounded-md px-3 py-1 text-xs font-medium"
            style={{ backgroundColor: 'var(--sidebar-active-bg)', color: 'var(--sidebar-active-fg)' }}
          >
            {t('tenants.primaryColorPreviewNav')}
          </span>
        </div>
      )}

      <p className="mt-2 text-xs text-muted-foreground">{t('tenants.primaryColorHint')}</p>

      {status && <FeldStatus status={status} />}
    </div>
  );
}
